import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  useCurrentUserPermissionsQuery,
  usePermissionsQuery,
} from 'queries/permissions';

interface UseAuthorizationV2Response {
  isAuthorized: boolean;
  isLoading: boolean;
  isError: boolean;
  error?: Error;
  displayName?: string;
}

/**
 * Hook to handle authorization logic based on user permissions.
 *
 * @param {string} required_permission - The permission required.
 * @returns {Object} An object containing:
 *   - `isAuthorized` {boolean}: Indicates whether the user is authorized.
 *   - `isLoading` {boolean}: Indicates whether the permissions are still loading.
 *   - `isError` {boolean}: Indicates whether there was an error while fetching permissions.
 *   - `error` {Error|undefined}: The error object if an error occurred, otherwise undefined.
 *
 * @example
 * const { isAuthorized, isLoading, isError, error } = useAuthorizationV2('view_dashboard');
 * if (isLoading) {
 *   return <LoadingSpinner />;
 * }
 * if (isError) {
 *   return <ErrorMessage error={error} />;
 * }
 * if (isAuthorized) {
 *   return <Dashboard />;
 * }
 * return <UnauthorizedMessage />;
 */
export const useAuthorizationV2 = (
  required_permission: string,
): UseAuthorizationV2Response => {
  const { rbacCandidateControl } = useFlags();
  const {
    data: userPermissions,
    isLoading,
    isError,
    error,
  } = useCurrentUserPermissionsQuery();

  const {
    data: permissions,
    isLoading: isPermissionsLoading,
    isError: isPermissionsError,
    error: permissionsError,
  } = usePermissionsQuery();

  if (!rbacCandidateControl) {
    return {
      isAuthorized: true,
      isLoading: false,
    } as UseAuthorizationV2Response;
  }

  if (!required_permission) {
    return {
      isAuthorized: true,
      isLoading: false,
    } as UseAuthorizationV2Response;
  }

  if (isLoading || isPermissionsLoading) {
    return {
      isAuthorized: false,
      isLoading: true,
    } as UseAuthorizationV2Response;
  }

  if (isError) {
    return {
      isAuthorized: false,
      isLoading: false,
      isError: isError || isPermissionsError,
      error: isError ? error : permissionsError,
    } as UseAuthorizationV2Response;
  }

  const displayName = permissions?.find(
    (permission) => permission.slug === required_permission,
  )?.display_name;

  if (!userPermissions || userPermissions.length === 0) {
    return {
      isAuthorized: false,
      isLoading: false,
      displayName: displayName,
    } as UseAuthorizationV2Response;
  }

  const hasPermission = userPermissions.some(
    (permission) => permission === required_permission,
  );

  return {
    isAuthorized: hasPermission,
    isLoading: false,
    displayName: displayName,
  } as UseAuthorizationV2Response;
};
